import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import { userSelector } from 'selectors/user/userSelector';
import Actions from 'Actions';
import {
    Flex, Button, Image, Text
} from 'components/ui';
import UserActions from 'actions/UserActions';
import agentAwareUtils from 'utils/AgentAware';

// This component shows the AgentAware banner with all the information about the user that is logged in
// Uses useSelector hook to fetch user data from Redux store
function CustomerBanner() {
    // Fetch user data using useSelector hook
    const user = useSelector(userSelector);

    const profile = {
        firstName: user.firstName,
        lastName: user.lastName,
        login: user.login || user.beautyInsiderAccount?.email,
        profileLocale: user.profileLocale || 'US'
    };

    const dispatch = useDispatch();

    const logoutConfirmCallback = () => {
        dispatch(UserActions.signOut('/', true, false));
    };

    const handleLogout = () => {
        dispatch(
            Actions.showInfoModal({
                isOpen: true,
                title: 'Are you sure you want to exit?',
                message: `Exiting client view will end your session as client ${profile.firstName} ${profile.lastName} (${profile.login}).`,
                buttonText: 'OK',
                callback: logoutConfirmCallback,
                showCancelButton: true,
                showCloseButton: true
            })
        );
    };

    const handleAddNotes = () => {
        dispatch(Actions.showAddNoteModal({ isOpen: true, noteType: 'profile' }));
    };

    if (!profile?.login || !agentAwareUtils.isAgentAwareSession()) {
        return null;
    }

    return (
        //hide AgentAware banner if extension is already activated to avoid double banners
        <div className='agent-aware-hide'>
            <Flex
                position='fixed'
                top={0}
                left={0}
                right={0}
                height='55px'
                paddingX='15px'
                backgroundColor='#FFF9E6'
                flexDirection='row'
                alignItems='center'
                fontSize='14px'
                fontWeight='700'
                gap='10px'
                css={styles.customer}
            >
                <Image
                    src={profile.profileLocale === 'US' ? '/img/ufe/flags/us.svg' : '/img/ufe/flags/ca.svg'}
                    alt='Flag'
                    css={styles.flag}
                />
                <>
                    <Text is='p'>{`You are viewing ${profile.firstName} ${profile.lastName}'s (${profile.login}) account.`}</Text>
                    <Button
                        variant='link'
                        onClick={handleLogout}
                        padding={0}
                        minWidth='auto'
                    >
                        <b>Sign out and exit Sephora.com</b>
                    </Button>
                    <Button
                        variant='secondary'
                        onClick={handleAddNotes}
                        paddingX='20px'
                        paddingY='5px'
                        css={styles.addNotesButton}
                    >
                        Add Account Notes
                    </Button>
                </>
            </Flex>
            {/* Spacer to prevent content from being hidden behind the fixed AgentAware banner */}

            <div style={{ height: '55px' }} />
        </div>
    );
}

const styles = {
    customer: {
        zIndex: 10000, // Ensure it's above everything else
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)' // Add shadow for visibility
    },
    flag: {
        width: '24px',
        height: '18px'
    },
    addNotesButton: {
        borderRadius: '25px',
        position: 'absolute',
        right: '15px'
    }
};

export default wrapFunctionalComponent(CustomerBanner, 'Customer');
